// Categoria -> cores acessíveis e distintas
// Todas as cores são HEX e pensadas para contraste AA com texto branco
export type CategoryColorSpec = {
  name: string;
  // Hex tokens (kept for tests and contrast computation)
  bg: string; // background HEX
  text: string; // text HEX
  border?: string; // optional border HEX
  // Tailwind utility classes for UI implementation
  bgClass: string;
  textClass: string;
  borderClass?: string;
  hoverClass?: string;
  icon?: string; // fontawesome icon class suffix
};

export const CATEGORY_COLORS: Record<string, CategoryColorSpec> = {
  'SAÚDE': {
    name: 'Saúde',
    bg: '#dcfce7', // green-100
    text: '#14532d', // green-900
    border: '#bbf7d0', // green-200
    bgClass: 'bg-green-100',
    textClass: 'text-green-900',
    borderClass: 'border-green-200',
    hoverClass: 'hover:bg-green-200',
    icon: 'fa-heart'
  },
  'ESPORTE': {
    name: 'Esporte',
    bg: '#e0f2fe', // sky-100
    text: '#0c4a6e', // sky-900
    border: '#bae6fd', // sky-200
    bgClass: 'bg-sky-100',
    textClass: 'text-sky-900',
    borderClass: 'border-sky-200',
    hoverClass: 'hover:bg-sky-200',
    icon: 'fa-futbol'
  },
  'AGRICULTURA': {
    name: 'Agricultura',
    bg: '#ecfccb', // lime-100
    text: '#365314', // lime-900
    border: '#d9f99d', // lime-200
    bgClass: 'bg-lime-100',
    textClass: 'text-lime-900',
    borderClass: 'border-lime-200',
    hoverClass: 'hover:bg-lime-200',
    icon: 'fa-seedling'
  },
  'EDUCAÇÃO': {
    name: 'Educação',
    bg: '#fef9c3', // yellow-100
    text: '#713f12', // yellow-900
    border: '#fef08a', // yellow-200
    bgClass: 'bg-yellow-100',
    textClass: 'text-yellow-900',
    borderClass: 'border-yellow-200',
    hoverClass: 'hover:bg-yellow-200',
    icon: 'fa-graduation-cap'
  },
  'CULTURA': {
    name: 'Cultura',
    bg: '#f3e8ff', // purple-100
    text: '#581c87', // purple-900
    border: '#e9d5ff', // purple-200
    bgClass: 'bg-purple-100',
    textClass: 'text-purple-900',
    borderClass: 'border-purple-200',
    hoverClass: 'hover:bg-purple-200',
    icon: 'fa-masks-theater'
  },
  'ASSISTÊNCIA SOCIAL': {
    name: 'Assistência Social',
    bg: '#fce7f3', // pink-100
    text: '#831843', // pink-900
    border: '#fbcfe8', // pink-200
    bgClass: 'bg-pink-100',
    textClass: 'text-pink-900',
    borderClass: 'border-pink-200',
    hoverClass: 'hover:bg-pink-200',
    icon: 'fa-hand-holding-heart'
  },
  'SEGURANÇA E TRÂNSITO': {
    name: 'Segurança e Trânsito',
    bg: '#ffedd5', // orange-100
    text: '#7c2d12', // orange-900
    border: '#fed7aa', // orange-200
    bgClass: 'bg-orange-100',
    textClass: 'text-orange-900',
    borderClass: 'border-orange-200',
    hoverClass: 'hover:bg-orange-200',
    icon: 'fa-shield'
  },
  'DEMAIS AÇÕES': {
    name: 'Demais Ações',
    bg: '#f5f5f5', // gray-100
    text: '#111827', // gray-900
    border: '#e5e7eb', // gray-200
    bgClass: 'bg-gray-100',
    textClass: 'text-gray-900',
    borderClass: 'border-gray-200',
    hoverClass: 'hover:bg-gray-200',
    icon: 'fa-tags'
  },
};

export const DEFAULT_CATEGORY_COLOR: CategoryColorSpec = {
  name: 'Categoria',
  bg: '#f1f5f9', // slate-100
  text: '#0f172a', // slate-900
  border: '#e2e8f0', // slate-200
  bgClass: 'bg-slate-100',
  textClass: 'text-slate-900',
  borderClass: 'border-slate-200',
  hoverClass: 'hover:bg-slate-200',
  icon: 'fa-tag',
};

const normalize = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().trim();

export function getCategoryColors(category?: string | null): CategoryColorSpec {
  if (!category) return DEFAULT_CATEGORY_COLOR;
  const raw = category.trim();
  // Tentativa direta primeiro
  if (CATEGORY_COLORS[raw]) return CATEGORY_COLORS[raw];
  // Igualdade ignorando acentos e caixa
  const norm = normalize(raw);
  for (const [k, spec] of Object.entries(CATEGORY_COLORS)) {
    if (normalize(k) === norm) return spec;
  }
  return DEFAULT_CATEGORY_COLOR;
}

// Util para validar contraste (WCAG AA)
export function contrastRatio(hex1: string, hex2: string): number {
  const toRgb = (hex: string) => {
    const h = hex.replace('#', '');
    const bigint = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return [r, g, b].map((v) => v / 255);
  };
  const relLum = (hex: string) => {
    const [r, g, b] = toRgb(hex).map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  const L1 = relLum(hex1);
  const L2 = relLum(hex2);
  const lighter = Math.max(L1, L2);
  const darker = Math.min(L1, L2);
  return (lighter + 0.05) / (darker + 0.05);
}
